/**
 * Cameras this page session has learned cannot play talk-down audio
 * (ENG-1721). A talk-down outcome with error_code no_backchannel,
 * unsupported_camera or no_audio_output (clip or live) means the camera has
 * no speaker /
 * backchannel, so the button is hidden for that camera and replaced by a
 * short note until reload.
 *
 * Module-level and deliberately NOT persisted: the backend's per-camera
 * `talk_down_capable` is the durable answer; this only stops an operator
 * from retrying a camera that just told us it can't play audio.
 */
import { create } from "zustand";

/** Talk-down outcome codes that mean "this camera has no speaker". */
export const NO_SPEAKER_ERROR_CODES: ReadonlySet<string> = new Set([
  "no_backchannel",
  "unsupported_camera",
  "no_audio_output",
]);

export const isNoSpeakerErrorCode = (code: unknown): boolean =>
  typeof code === "string" && NO_SPEAKER_ERROR_CODES.has(code);

export const TALK_DOWN_NO_SPEAKER_NOTE = "This camera has no speaker";

interface TalkDownCapabilityState {
  /** camera id -> learned not capable this page session */
  incapable: Record<string, true>;
  markIncapable: (cameraId: string | number) => void;
  reset: () => void;
}

export const useTalkDownCapabilityStore = create<TalkDownCapabilityState>(
  (set) => ({
    incapable: {},
    markIncapable: (cameraId) =>
      set((state) => {
        const key = String(cameraId);
        if (state.incapable[key]) return state;
        return { incapable: { ...state.incapable, [key]: true } };
      }),
    reset: () => set({ incapable: {} }),
  })
);

/** Record a talk-down outcome's error code; no-op unless it means "no speaker". */
export const noteTalkDownErrorCode = (
  cameraId: string | number | null | undefined,
  code: unknown
): void => {
  if (cameraId == null || cameraId === "" || !isNoSpeakerErrorCode(code))
    return;
  useTalkDownCapabilityStore.getState().markIncapable(cameraId);
};

/** True once this page session has learned the camera has no speaker. */
export const useTalkDownLearnedIncapable = (
  cameraId: string | number | null | undefined
): boolean =>
  useTalkDownCapabilityStore((state) =>
    cameraId == null || cameraId === ""
      ? false
      : Boolean(state.incapable[String(cameraId)])
  );
